import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { ROUTES } from "routes/constants";
import type { GIWLApplicationFormData } from "types/Card";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
    resetApplication,
    saveLandingPageData,
    saveSessionId,
    setGiwlHydratedParamsKey,
    updateOfferData,
} from "store/application/applicationSlice";
import {
    buildGIWLQuotePayload,
    hasGIWLQuoteParams,
    parseGIWLCampaignParams,
    parseGIWLQuoteParams,
    resolveGIWLState,
} from "utils/giwlQuoteParams";
import { getFirst30CharsOfSessionId, getSessionIdFromSearchParams } from "utils/sessionIdUtils";
import { storeSessionId } from "services/onlineApplicationService";

interface UseGIWLQuoteHydrationArgs {
    isGIWL: boolean;
    landingPageUrl: string;
    giwlLandingParamsKey: string;
    giwlForm: UseFormReturn<GIWLApplicationFormData>;
    saveQuote: (quote: ReturnType<typeof buildGIWLQuotePayload>) => void;
}

/**
 * Restores GIWL quote/contact fields from the landing-page URL params when the
 * user deep-links into the application. Hydrates whenever valid landing params
 * are present that differ from the last hydrated set (a fresh landing, even if
 * it re-lands over an existing quote). Params identical to the last hydration
 * (tracked in Redux, so it survives navigation and refresh) are skipped — the
 * query string now persists across in-app navigation, and re-visiting the page
 * with the same params must not reset the in-progress application. When the URL
 * carries no quote params at all, it's a no-op if a quote already lives in
 * Redux (back-navigation, so in-progress data is preserved). A landing whose
 * params are present but invalid/incomplete always routes to the error page —
 * even over a stale quote persisted from a prior session. Cancels in-flight async
 * resolution on unmount.
 */
export const useGIWLQuoteHydration = ({
    isGIWL,
    landingPageUrl,
    giwlLandingParamsKey,
    giwlForm,
    saveQuote,
}: UseGIWLQuoteHydrationArgs) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const quoteData = useAppSelector((state) => state.application.quoteData);
    const giwlHydratedParamsKey = useAppSelector((state) => state.application.giwlHydratedParamsKey);

    /**
     * Latest quote/hydrated-key read through refs so the effect sees current values
     * without making them effect dependencies. The effect itself resets/sets both,
     * so depending on them would re-run (and cancel its own in-flight hydrate) on
     * every change, churning the reset+hydrate cycle.
     */
    const quoteDataRef = useRef(quoteData);
    quoteDataRef.current = quoteData;
    const hydratedParamsKeyRef = useRef(giwlHydratedParamsKey);
    hydratedParamsKeyRef.current = giwlHydratedParamsKey;

    useEffect(() => {
        if (!isGIWL || landingPageUrl.includes("contact-information")) return;
        if (hydratedParamsKeyRef.current === giwlLandingParamsKey) return;

        const parsed = parseGIWLQuoteParams(giwlLandingParamsKey);
        if (!parsed.valid) {
            // The URL carries no quote params at all, so this isn't a landing. When a
            // quote already lives in Redux the user navigated back into the flow (e.g.
            // from review/payment) — keep their in-progress data. We deliberately gate
            // on param-presence (not just quoteData) so a fresh landing with incomplete
            // params still errors even when a stale quote is persisted from a prior
            // session, instead of being silently treated as back-navigation.
            const isBackNavigation = !!quoteDataRef.current && !hasGIWLQuoteParams(giwlLandingParamsKey);
            if (isBackNavigation) return;

            console.error("ContactInformationPage: Missing required GIWL data", { giwlLandingParamsKey });
            navigate(ROUTES.SOMETHING_WENT_WRONG);
            return;
        }

        dispatch(resetApplication());
        // A fresh landing must not inherit form values seeded from a previous
        // application (rehydrated from sessionStorage after an outcome, or still
        // in memory after back-navigation) — the cards' hydrate effects only fill,
        // never clear, so reset the form to its pristine defaults here before
        // applying the landing params below.
        giwlForm.reset();

        const campaign = parseGIWLCampaignParams(giwlLandingParamsKey);
        // campaignUrl records the URL actually landed with, so outbound links can
        // forward only the campaign params that were really present (not defaults).
        dispatch(saveLandingPageData({ campaign: { ...campaign, campaignUrl: landingPageUrl } }));
        dispatch(updateOfferData({ campaignCode: campaign.campaignCode }));

        // Bind the session: GIWL does not mint its own ID — it always reads the
        // sessionId from the URL, derives the 30-char saveContId, and registers it via
        // storeSessionId. The session is sourced purely from the landing params (never
        // from a previously generated id), so this overrides whatever resetApplication
        // regenerated above with the canonical id from the URL.
        const searchParams = new URLSearchParams(giwlLandingParamsKey);
        const sessionIdFromUrl = getSessionIdFromSearchParams(searchParams);
        const saveContId = getFirst30CharsOfSessionId(sessionIdFromUrl ?? undefined);

        if (saveContId) {
            dispatch(saveSessionId({ sessionId: sessionIdFromUrl!, saveContId }));
            storeSessionId({ userRef: saveContId, saveContId }).catch((error) => {
                console.error("[useGIWLQuoteHydration] Failed to bind session:", error);
            });
        }

        let isCancelled = false;

        const hydrateGIWLQuote = async () => {
            const { params } = parsed;
            const { state, zipCode } = await resolveGIWLState(params.stateFromParams, params.zipCode);

            if (isCancelled) return;

            const email = params.email || "";
            giwlForm.setValue("contactInformation.email", email);
            giwlForm.setValue("contactInformation.zipCode", zipCode);
            giwlForm.setValue("contactInformation.gender", params.gender);

            saveQuote(buildGIWLQuotePayload(params, state, zipCode));
            dispatch(setGiwlHydratedParamsKey(giwlLandingParamsKey));
        };

        hydrateGIWLQuote().catch((error) => {
            console.error("Failed to hydrate GIWL quote:", error);
        });

        return () => {
            isCancelled = true;
        };
    }, [isGIWL, landingPageUrl, giwlLandingParamsKey, navigate, saveQuote, giwlForm, dispatch]);
};
